import React from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';

type PaddingVariant = 'none' | 'default' | 'compact';
type ContentGapVariant = 'none' | 'sm' | 'md' | 'lg';

interface PageContainerProps extends ForwardedHTMLProps {
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  padding?: PaddingVariant;
  contentGap?: ContentGapVariant;
  className?: string;
}

const maxWidthClasses = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

const paddingClasses: Record<PaddingVariant, string> = {
  none: '',
  default: 'py-8 px-4 sm:px-6 lg:px-8',
  compact: 'py-4 sm:py-6 px-4 sm:px-6 lg:px-8',
};

const contentGapClasses: Record<ContentGapVariant, string> = {
  none: '',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
};

export const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  ({ children, maxWidth = 'xl', padding = 'default', contentGap = 'md', className, ...props }, ref) => {
    const forwardedProps = forwardProps(props);
    const hasContentGap = contentGap !== 'none';

    return (
      <div
        ref={ref}
        className={cn(
          'w-full mx-auto',
          paddingClasses[padding],
          maxWidthClasses[maxWidth],
          hasContentGap && 'flex flex-col',
          hasContentGap && contentGapClasses[contentGap],
          className
        )}
        {...forwardedProps}
        {...props}
      >
        {children}
      </div>
    );
  }
);

PageContainer.displayName = 'PageContainer';
